import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/esg.png';
import { Leaf, Users, Shield, AlertTriangle, Target, Award } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/esg');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/esg-transition-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/esg-controversy-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/institutional-health-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/catalyst-radar-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const ESGDatapage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="ESG Data API – ESG Scores & Sustainability Ratings"
        description="ESG data API with scores and sustainability ratings for 15,000+ companies: environmental, social and governance data covering carbon, diversity and compliance."
        canonicalUrl="https://axionquant.com/products/esg"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion ESG Data API",
          "description": "ESG data API with ESG scores and sustainability ratings for 15,000+ companies. Environmental, social, and governance data covering carbon, diversity, and compliance.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "ESG Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>
     <Header
  docs="/docs/02-alternative-data/ESG"
  title="ESG Data API for Sustainable Investment Intelligence"
  body="Access ESG scores and ratings through our ESG data API, with carbon footprint analysis and sustainability data for 15,000+ companies. Integrate environmental, social, and governance data into investment decisions and meet regulatory reporting requirements including SFDR, TCFD, and EU Taxonomy compliance."
  crumb={{name: 'ESG', path:'/products/esg'}}
  tabs={[
    {
      label: 'ESG Screener',
      language: 'python',
      filename: 'esg_screener.py',
      bg: '6.jpeg',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

universe = ["MSFT", "AAPL", "GOOGL", "AMZN", "META", "NVDA", "TSLA"]

esg_data = {
    ticker: client.esg.data(ticker)
    for ticker in universe
}

screened = [
    {
        "ticker":      ticker,
        "total":       data["totalEsg"],
        "environment": data["environmentScore"],
        "social":      data["socialScore"],
        "governance":  data["governanceScore"],
        "percentile":  data["percentile"],
        "performance": data["esgPerformance"],
    }
    for ticker, data in esg_data.items()
    if data["esgPerformance"] != "UNDER_PERF"
]

ranked = sorted(screened, key=lambda x: x["total"])
`,
    },
    {
      label: 'ESG vs. Financials',
      language: 'python',
      filename: 'esg_vs_financials.py',
      bg: '4.jpeg',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

tickers = ["MSFT", "AAPL", "GOOGL", "AMZN", "META"]

combined = [
    {
        "ticker":      t,
        "esg_total":   client.esg.data(t)["totalEsg"],
        "esg_env":     client.esg.data(t)["environmentScore"],
        "roe":         client.financials.metrics(t)["returnOnEquity"],
        "gross_margin":client.financials.metrics(t)["grossMargin"],
        "fcf":         client.financials.free_cash_flow(t, periods=1)[0]["value"],
    }
    for t in tickers
]

by_esg_risk   = sorted(combined, key=lambda x: x["esg_total"])
by_roe        = sorted(combined, key=lambda x: x["roe"], reverse=True)
`,
    },
    {
      label: 'ESG Peer Comparison',
      language: 'python',
      filename: 'esg_peer_comparison.py',
      bg: '22.jpg',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

ticker = "TSLA"

peers = client.supply_chain.peers(ticker)
peer_tickers = [p["ticker"] for p in peers[:5]]

focus   = client.esg.data(ticker)
peer_scores = {
    p: client.esg.data(p)
    for p in peer_tickers
}

relative = [
    {
        "ticker":     p,
        "total":      data["totalEsg"],
        "env_delta":  data["environmentScore"] - focus["environmentScore"],
        "gov_delta":  data["governanceScore"]  - focus["governanceScore"],
        "percentile": data["percentile"],
    }
    for p, data in peer_scores.items()
]

ranked = sorted(relative, key=lambda x: x["total"])
`,
    },
    {
      label: 'ESG Sentiment Divergence',
      language: 'python',
      filename: 'esg_sentiment_divergence.py',
      bg: '21.jpg',
      code: `from axion import Axion

client = Axion(api_key="your_api_key")

tickers = ["XOM", "CVX", "BP", "SHEL", "COP"]

divergence = [
    {
        "ticker":      t,
        "esg_total":   client.esg.data(t)["totalEsg"],
        "esg_pct":     client.esg.data(t)["percentile"],
        "news_score":  client.sentiment.news(t)["score"],
        "social_score":client.sentiment.social(t)["score"],
    }
    for t in tickers
]

flagged = [
    row for row in divergence
    if row["esg_pct"] < 30 and row["social_score"] > 0.5
]
`,
    },
  ]}
/>
      <Marquee/>

      <FeaturesGrid
        title="ESG Data API. "
        subtitle="Environmental, social, and governance data, ESG scores, and sustainability ratings. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Leaf size={80} color="var(--green)" />,
          title: 'ESG Scores & Ratings',
          description: 'Comprehensive ESG scores and ratings API data with pillar-level breakdowns (Environmental, Social, Governance) for 15,000+ companies and full transparency into underlying metrics.',
        },
        {
          fig: 'FIG 0.2',
          icon: <AlertTriangle size={80} color="var(--red)" />,
          title: 'Controversy Monitoring',
          description: 'Updated daily signals on ESG controversies, lawsuits, and regulatory violations from alternative ESG data that often precede rating downgrades or market reactions.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Users size={80} color="var(--black)" />,
          title: 'Diversity & Labor Metrics',
          description: 'Board diversity statistics, labor practices, human rights assessments, and employee satisfaction scores within our sustainability data API.',
        },
        {
          fig: 'FIG 0.4',
          icon: <Shield size={80} color="var(--purple)" />,
          title: 'Governance & Ethics',
          description: 'Board independence, executive compensation, shareholder rights, and corporate ethics governance data for robust analysis.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Target size={80} color="var(--grey)" />,
          title: 'SDG Alignment',
          description: 'UN Sustainable Development Goal alignment scores and impact measurement frameworks to quantify societal contributions.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Award size={80} color="var(--green)" />,
          title: 'Certifications & Compliance',
          description: 'B Corp status, ISO certifications, and regulatory compliance indicators (SFDR, TCFD, EU Taxonomy) for sustainable investing mandates.',
        },
      ]}/>



      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="ESG Data Platform"
        headingPlain="ESG data"
        headingEm="for 15,000 companies."
        body="Institutional-grade ESG data and supply chain intelligence - normalized, auditable, and ready for regulatory reporting."
        stats={[
          { v: "15,000+", l: "Companies" },
          { v: "500+",    l: "Data Points" },
          { v: "10+",     l: "Frameworks" },
          { v: "95%",     l: "S&P 500" },
        ]}
        markers ={ [

          // ESG ranking / rating institutions
          { id: "msci",            location: [40.71, -74.01],  label: "MSCI (ESG Ratings)" },
          { id: "sustainalytics",  location: [52.38, 4.90],    label: "Sustainalytics (Morningstar)" },
          { id: "iss-esg",         location: [39.08, -77.15],  label: "ISS ESG" },
          { id: "sp-global",       location: [40.71, -74.01],  label: "S&P Global (ESG Scores)" },
          { id: "ftse-russell",    location: [51.52, -0.10],   label: "FTSE Russell (ESG Ratings)" },
          { id: "moodys-esg",      location: [40.71, -74.01],  label: "Moody's ESG Solutions" },
          { id: "refinitiv",       location: [51.50, -0.02],   label: "Refinitiv (LSEG)" },
          { id: "cdp",             location: [51.51, -0.08],   label: "CDP" },
          { id: "gri",             location: [52.33, 4.87],    label: "Global Reporting Initiative (GRI)" },
          { id: "sasb",            location: [37.79, -122.40], label: "SASB (now IFRS Foundation)" },
          { id: "issb",            location: [51.51, -0.10],   label: "ISSB (IFRS Foundation)" },
          { id: "unpri",           location: [51.51, -0.07],   label: "UN PRI" },

          // Regulatory bodies and standard-setters
          { id: "sec",             location: [38.90, -77.00],  label: "SEC (US)" },
          { id: "esma",            location: [48.84, 2.38],    label: "ESMA (EU)" },
          { id: "eba",             location: [48.89, 2.24],    label: "EBA (EU)" },
          { id: "fsb",             location: [47.55, 7.59],    label: "Financial Stability Board" },
          { id: "iosco",           location: [40.43, -3.69],   label: "IOSCO" },
          { id: "ngfs",            location: [48.86, 2.34],    label: "NGFS (Network for Greening the Financial System)" }
        ]}
      />

      <DataGrid
        title="ESG Data API That Goes Beyond the Score"
        subtitle="Move past superficial ratings with comprehensive environmental, social, and governance data, sentiment analysis, regulatory filings, controversy monitoring, and supply chain insights. Quantify intangible risks and opportunities with structured, auditable metrics."
        bg={databg}
        stats={[
          { label: 'ESG Metrics Tracked', value: '1,200+' },
          { label: 'Companies Monitored', value: '15,000+' },
          { label: 'Controversy Signals', value: 'Current' },
        ]}
      />
      <CardList
        title="Build Sustainable Alpha with ESG Data"
        vid={'https://cdn.studio14a.com/axion/products/cards/esg.mp4'}
        points={[
          {
            "title": "Granular Factor & Metric Data",
            "details": "Access underlying ESG pillar scores (Environmental, Social, Governance) and hundreds of individual metrics - from carbon emissions and board diversity to labor practices - not just a single composite score.",
            "cta": "Explore ESG factors",
            "url": "/docs"
          },
          {
            "title": "Sentiment & Controversy Monitoring",
            "details": "Track current ESG sentiment from news, social media, and regulatory bodies. Surface controversies, lawsuits, and negative press that often precede rating downgrades or market reactions.",
            "cta": "See sentiment analysis",
            "url": "/products/sentiment"
          },
          {
            "title": "Supply Chain & Scope 3 Intelligence",
            "details": "Map ESG risk exposure across the entire value chain. Analyze supplier ESG scores to identify concentration risk and potential disruptions hidden from traditional financials.",
            "cta": "Analyze supply chain risk",
            "url": "/products/supply-chain"
          }
        ]}
      />

      <Responses
        heading='From raw data to risk signal'
        subheading='One request returns the full ESG picture for a company - overall score, pillar-level breakdowns, and controversy exposure, all graded and ready to plug into your models.'
        ticker='AAPL'
        tabs={[
          {
            title: 'ESG Scores',
            description: 'Overall ESG rating and environmental, social, governance pillar breakdowns',
            data: [
              {
                "category": "esg",
                "score": 42.86,
                "grade": "BB",
                "id": "1"
              },
              {
                "category": "social",
                "score": 40,
                "grade": "BB",
                "id": "5"
              },
              {
                "category": "governance",
                "score": 31,
                "grade": "BB",
                "id": "6"
              },
              {
                "category": "controversy",
                "score": 20,
                "grade": "B",
                "id": "3"
              },
              {
                "category": "environment",
                "score": 57,
                "grade": "BBB",
                "id": "4"
              }
            ],
          },
        ]}
      />

      <DatasetDetails full={true}
        title="Comprehensive ESG Ratings & Sustainability Coverage"
        subtitle="Access ESG ratings, carbon footprint analysis, and sustainability metrics via our ESG data API covering 15,000+ companies globally. Helps asset managers build sustainable portfolios, conduct impact analysis, and meet regulatory reporting requirements including SFDR, TCFD, and EU Taxonomy compliance."
        cta="Begin Free Trial"
        ctaLink="/login"
        tiles={[
          {
            img: '10.webp',
            title: 'We rate 15,000+ companies',
            caption: 'You can access ESG scores, ratings, carbon footprints, and sustainability metrics for global companies.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '5.webp',
            title: 'We provide 500+ data points per company',
            caption: 'You can conduct deep ESG analysis with comprehensive environmental, social, and governance metrics.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '3.webp',
            title: 'We align with 10+ frameworks',
            caption: 'You can meet regulatory requirements including SFDR, TCFD, and EU Taxonomy compliance.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '12.webp',
            title: 'We cover 95% of the S&P 500',
            caption: 'You get deep ESG metrics for the vast majority of US large-cap companies.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '14.webp',
            title: 'We update controversy signals daily',
            caption: 'You can monitor ESG risks with current signals on controversies and emerging issues.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Numbers that speak for themselves."
        leftCell={{ stat: '15,000+', title: 'Companies Monitored.', desc: 'Deep ESG and sustainability data across global equities.', showChart: true }}
        rightCells={[
          { stat: '500+', title: 'Data Points per Company.', desc: 'From carbon to diversity.' },
          { stat: 'Daily', title: 'Controversy Signals.', desc: 'Signals that precede downgrades.', boldDesc: true },
        ]}

      /><ProductFAQ slug="esg" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default ESGDatapage;
